import debounce from 'lodash/debounce';
import React, { useCallback, useEffect, useState, ChangeEvent, forwardRef, ForwardedRef, useImperativeHandle } from 'react';
import noop from 'lodash/noop';
import cn from 'classnames';
import Input from '@dealroadshow/uikit-input';
import { IconComponentType } from '@dealroadshow/uikit-icon';
import IconFiltering from '@dealroadshow/uikit-icon/IconFiltering';
import styles from './search-input.module.scss';

export interface IProps {
  debounceWaitTime?: number,
  value: string,
  placeholder: string,
  isNarrow?: boolean,
  isClearable?: boolean,
  inputClassName?: string,
  iconClassName?: string,
  icon?: IconComponentType,
  formFieldClassName?: string,
  dataTest?: string,
  disabled?: boolean,
  className?: string,
  onInit?: () => void,
  onChange: (value: any) => void,
  reset?: () => void,
  size?: 'small' | 'medium',
}

export type TSearchInputRef = {
  handleClearInput: () => void,
};

const SearchInput = forwardRef((
  props: IProps,
  parentRef: ForwardedRef<TSearchInputRef>,
) => {
  const {
    value,
    dataTest,
    isNarrow = true,
    isClearable = true,
    icon = IconFiltering,
    debounceWaitTime = 500,
    onInit = noop,
    onChange = noop,
    reset = noop,
  } = props;

  const [localValue, setLocalValue] = useState<string>(value);
  const debouncedOnChange = debounce(onChange, debounceWaitTime);

  useImperativeHandle<TSearchInputRef, TSearchInputRef>(
    parentRef,
    () => ({ handleClearInput }),
    [],
  );

  useEffect(() => {
    return () => {
      reset();
      setLocalValue(null);
    };
  }, []);

  const handleSearchChange = useCallback((e: ChangeEvent<HTMLInputElement>) => {
    setLocalValue(e.target.value);
    debouncedOnChange(e.target.value);
  }, [debounceWaitTime]);

  const handleClearInput = () => {
    setLocalValue('');
    onChange('');
  };

  return (
    // @ts-ignore
    <Input
      { ...props }
      className={ cn(styles.searchInput, props.className) }
      onChange={ handleSearchChange }
      value={ localValue }
      dataTest={ dataTest || 'formSearchInput' }
      isNarrow={ isNarrow }
      isClearable={ isClearable }
      icon={ icon }
      onInit={ onInit }
    />
  );
});

export default SearchInput;
